import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/seisveinte/Table/TableUI'
import { HeaderCell } from '@/components/seisveinte/Table/HeaderCell'
import { ManageFilters } from '@/components/seisveinte/Table/ManageFilters'
import { NoData } from '@/components/seisveinte/Table/NoData'
import { TablePagination } from '@/components/seisveinte/Table/TablePagination'
import { filterFns } from '@/components/seisveinte/Table/filterFns'
import { makeManageFilterOptions } from '@/components/seisveinte/Table/utils'
import { TableHeaderCellOptions } from '@/components/seisveinte/Table/table.types'
import { useTableState } from '@/components/seisveinte/Table/useTableState'
import { DocumentNode, OperationVariables, TypedDocumentNode, useLazyQuery } from '@apollo/client'

interface TableViewProps<TData> {
  query: DocumentNode | TypedDocumentNode<any, OperationVariables>
  columns: ColumnDef<TData>[]
  headerCellOptions: TableHeaderCellOptions
  customHeader?: React.ReactNode
  formatData?: (data: any[]) => TData[]
  extraFilters?: Record<string, any>
}

function PaginatedTable<TData>({
  query,
  columns,
  headerCellOptions,
  customHeader = null,
  formatData = (data: any[]) => data,
  extraFilters = {},
}: TableViewProps<TData>) {
  const [getData, { loading }] = useLazyQuery(query, { fetchPolicy: 'network-only' })
  const [data, setData] = useState<TData[]>([])
  const [paginationCount, setPaginationCount] = useState(0)
  const { columnFilters, pagination, setColumnFilters, setPagination, setSorting, sorting } =
    useTableState()

  const table = useReactTable({
    columns,
    data,
    filterFns: filterFns,
    getCoreRowModel: getCoreRowModel(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    state: {
      columnFilters,
      pagination,
      sorting,
    },
    manualPagination: true,
    manualFiltering: true,
    pageCount: paginationCount,
  })

  const manageFilterOptions = makeManageFilterOptions(headerCellOptions)

  const getTableData = () => {
    // Extract pagination data
    const { pageIndex, pageSize } = pagination

    // Map sorting and columnFilters variables and create the requestParams
    const sortingParams = sorting.map((sort) => `${sort.desc ? '-' : ''}${sort.id}`)
    const filterParams = columnFilters.reduce((acc, filter) => {
      const { id, value } = filter
      if (Array.isArray(value)) {
        return { ...acc, [id]: value.join(',') }
      }
      if (typeof value === 'object' && value !== null && 'from' in value && 'to' in value) {
        if (value.from && value.to) {
          return { ...acc, [`${id}From`]: value.from, [`${id}To`]: value.to }
        } else if (value.from) {
          return { ...acc, [id]: value.from }
        } else {
          return acc
        }
      }
      return { ...acc, [id]: value }
    }, {})

    const filters = {
      ...filterParams,
      ...extraFilters,
    }

    // Get data from backend with requestParamas
    const requestParams = {
      orderBy: sortingParams.join(',') || undefined,
      page: pageIndex + 1, // Backend pagination usually starts at 1, pageIndex starts at 0
      pageSize: pageSize,
      filters: Object.keys(filters).length ? filters : undefined,
    }

    getData({ variables: requestParams }).then((response) => {
      const newData = response.data[Object.keys(response.data)[0]]
      const pageMeta = newData.pageMeta
      const key = Object.keys(newData).find((key) => key !== 'pageMeta' && key !== '__typename')
      const data = key ? newData[key] : []
      setData(formatData(data))
      setPaginationCount(pageMeta.totalPages)
    })
  }

  useEffect(() => {
    // Get data from backend when sorting, column filters or global date filter change
    getTableData()
  }, [columnFilters, sorting, pagination, extraFilters])

  return (
    <div className="grid h-full w-full grid-cols-[1fr] grid-rows-[min-content_min-content_min-content] gap-3">
      <div className="mb-3 flex h-full w-full items-center justify-between pt-2">
        <div className="flex h-fit w-full justify-end gap-6">
          <ManageFilters columnFilters={columnFilters} table={table} {...manageFilterOptions} />
        </div>
      </div>
      {customHeader}
      <div className="flex h-full w-full flex-col justify-between gap-5 overflow-hidden">
        <Table className="max-h-full border-collapse table-fixed">
          <TableHeader className="[&_tr]:border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const styleGroupHeader = header.id === 'acciones' ? 'text-center' : ''
                  return (
                    <TableHead
                      className={styleGroupHeader}
                      colSpan={header.colSpan}
                      key={header.id}
                    >
                      {header.isPlaceholder ? null : header.column.columnDef.id === 'select' ? (
                        <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                      ) : (
                        <HeaderCell
                          column={header.column}
                          key={header.column.id}
                          table={table}
                          {...headerCellOptions[header.column.id as keyof typeof headerCellOptions]}
                        />
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell className="h-[200px] text-center" colSpan={columns.length}>
                  Cargando...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow data-state={row.getIsSelected() && 'selected'} key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cell.column.id === 'acciones' ? 'w-[80px]' : 'w-auto'}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell className="h-[200px]" colSpan={columns.length}>
                  <NoData />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex h-fit w-full justify-between">
        <TablePagination table={table} />
      </div>
    </div>
  )
}

export { PaginatedTable }
